import React, { useState, useRef, useEffect } from 'react';
import { FaPhoneVolume } from 'react-icons/fa';
import { BottomSheet } from 'react-spring-bottom-sheet';
import { toast } from 'react-toastify';
import { FaEdit } from 'react-icons/fa';
import '../index.css';

function LoginModal({ setModalOn }) {
    const [phoneNumber, setPhoneNumber] = useState('');
    const [showSuccess, setShowSuccess] = useState(false);
    const [verificationCode, setVerificationCode] = useState(['', '', '', '']);
    const phoneInputRef = useRef(null);
    const [formErrors, setFormErrors] = useState({});
    const [shake, setShake] = useState(false);
    const activeToasts = useRef(new Set());
    const codeInputRefs = useRef([]);

    useEffect(() => {
        if (shake) {
            const timer = setTimeout(() => {
                setShake(false);
            }, 1000);
            return () => clearTimeout(timer);
        }
    }, [shake]);

    useEffect(() => {
        if (showSuccess) {
            // التركيز على أول مربع من اليسار عند فتح نافذة التحقق
            setTimeout(() => {
                codeInputRefs.current[0]?.focus();
            }, 100);
        }
    }, [showSuccess]);

    useEffect(() => {
        const scrollY = window.scrollY;
        document.body.style.overflow = 'hidden';
        document.body.style.position = 'fixed';
        document.body.style.top = `-${scrollY}px`;
        document.body.style.width = '100%';
        
        return () => {
            document.body.style.overflow = '';
            document.body.style.position = '';
            document.body.style.top = '';
            document.body.style.width = '';
            window.scrollTo(0, scrollY);
        };
    }, []);

    const showToast = (message) => {
        if (!activeToasts.current.has(message)) {
            activeToasts.current.add(message);
            toast.error(message, {
                onClose: () => activeToasts.current.delete(message),
                autoClose: 3000
            });
        }
    };

    const handlePhoneChange = (e) => {
        const value = e.target.value.replace(/\D/g, '');
        if (value.length <= 10) {
            setPhoneNumber(value);
            setFormErrors({ ...formErrors, phone: '' });
        }
    };

    const handleVerificationCodeChange = (index, value) => {
    const cleanValue = value.replace(/\D/g, '');
    const newCode = [...verificationCode];

    if (cleanValue.length === 1) {
        newCode[index] = cleanValue;
    } else if (cleanValue.length > 1) {
        cleanValue.split('').forEach((digit, i) => {
            if (index + i < 4) {
                newCode[index + i] = digit;
            }
        });
    } else {
        newCode[index] = ''; // إذا كان المستخدم يحذف القيمة بالكامل
    }

    setVerificationCode(newCode);

    // الانتقال إلى الحقل التالي فقط عند إدخال رقم
    if (cleanValue.length === 1 && index < 3) {
        codeInputRefs.current[index + 1]?.focus();
    }
};


    const handlePaste = (e, index) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData('text').replace(/\D/g, '');
        if (pastedData) {
            const newCode = [...verificationCode];
            pastedData.split('').forEach((digit, i) => {
                if (index + i < 4) {
                    newCode[index + i] = digit;
                }
            });
            setVerificationCode(newCode);

            const nextIndex = Math.min(index + pastedData.length, 3);
            codeInputRefs.current[nextIndex]?.focus();
        }
    };

    const handleKeyDown = (index, e) => {
        if (e.key === 'Backspace') {
            if (verificationCode[index] === '' && index > 0) {
                // الانتقال إلى الحقل السابق إذا كان الحقل الحالي فارغًا
                codeInputRefs.current[index - 1]?.focus();
                const newCode = [...verificationCode];
                newCode[index - 1] = ''; // حذف الرقم في الحقل السابق
                setVerificationCode(newCode);
            } else if (verificationCode[index] !== '') {
                // حذف الرقم في الحقل الحالي
                const newCode = [...verificationCode];
                newCode[index] = '';
                setVerificationCode(newCode);
            }
        }
    };
    

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!phoneNumber) {
            setFormErrors({ ...formErrors, phone: 'الرجاء إدخال رقم الجوال' });
            setShake(true);
            showToast('الرجاء إدخال رقم الجوال');
            return;
        }
        if (phoneNumber.length !== 10) {
            setFormErrors({ ...formErrors, phone: 'يجب أن يتكون رقم الجوال من 10 أرقام' });
            setShake(true);
            showToast('يجب أن يتكون رقم الجوال من 10 أرقام');
            return;
        }
        setShowSuccess(true);
    };

    // التحكم في موضع المؤشر
    const handlePhoneFocus = (e) => {
        setTimeout(() => {
            if (phoneInputRef.current) {
                const length = phoneInputRef.current.value.length;
                phoneInputRef.current.setSelectionRange(length, length);
            }
        }, 0);
    };

    return (
        <BottomSheet
            open={true}
            onDismiss={() => setModalOn(false)}
            snapPoints={({ minHeight }) => [minHeight]}
            blocking={true}
            scrollLocking={true}
            defaultSnap={0}
            expandOnContentDrag={true}
            sibling={
                <div 
                    className="fixed inset-0 z-[400]"
                    style={{ backdropFilter: 'blur(3px) saturate(80%)' }}
                />
            }
        >
            <div dir="rtl">
                <div className="p-6">
                    {!showSuccess && (
                        <>
                            <h2 className="text-2xl font-semibold text-center mb-2 text-main-900">تسجيل الدخول</h2>
                            <p className="text-gray-600 text-center mb-6">الرجاء إدخال رقم الجوال للمتابعة</p>
                            <div className="w-full h-px bg-gray-200 mb-6"></div>
                        </>
                    )}
                    
                    {!showSuccess ? (
                        <form onSubmit={handleSubmit} className="space-y-4 mt-6">
                            <div className={`relative ${(formErrors?.phone && shake) ? 'animate-shake' : ''}`}>
                                <input
                                    ref={phoneInputRef}
                                    type="tel"
                                    id="phoneNumber"
                                    name="phoneNumber"
                                    autoComplete="off"
                                    maxLength="10"
                                    enterKeyHint="done"
                                    dir="rtl"
                                    style={{ textAlign: 'right', direction: 'rtl' }}
                                    value={phoneNumber}
                                    onChange={handlePhoneChange}
                                    onFocus={handlePhoneFocus}
                                    className={`peer w-full h-12 pr-10 p-3 border-2 rounded-xl bg-transparent outline-none transition-all duration-300 placeholder:text-gray-400 placeholder:opacity-0 focus:placeholder:opacity-100 ${
                                        (formErrors?.phone && shake) ? 'border-red-500 text-red-500' : 'border-gray-300 focus:border-main-900 text-gray-900 dark:text-white dark:border-gray-600'
                                    }`}
                                    placeholder="مثال : 0555555555"
                                />
                                <FaPhoneVolume className={`absolute right-3 top-1/2 -translate-y-1/2 text-lg ${
                                    (formErrors?.phone && shake) ? 'text-red-500' : 'text-gray-400 peer-focus:text-main-900'
                                } z-20`} />
                                <label 
                                    htmlFor="phoneNumber"
                                    className={`absolute text-sm leading-none duration-300 transform -translate-y-4 top-2 z-10 origin-[0] bg-white dark:bg-gray-700 px-2 peer-focus:px-2 peer-placeholder-shown:scale-100 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:top-1/2 peer-focus:top-2 peer-focus:scale-100 peer-focus:text-sm peer-focus:leading-none peer-focus:-translate-y-4 right-10 peer-placeholder-shown:bg-transparent peer-focus:bg-white dark:peer-focus:bg-gray-700 ${
                                        (formErrors?.phone && shake) ? 'text-red-500' : 'text-gray-400 peer-focus:text-main-900'
                                    }`}
                                >
                                    رقم الجوال
                                </label>
                            </div>
                            
                            <button
                                type="submit"
                                className="w-full bg-main-900 text-white py-3 rounded-xl hover:bg-main-800 transition-colors duration-300"
                            >
                                متابعة
                            </button>
                        </form>
                    ) : (
                        <div className="space-y-6">
                            <div className="text-center">
                                <h3 className="text-lg font-medium text-gray-900 mb-2">أدخل رمز التحقق</h3>
                                <p className="text-gray-500 text-sm mt-2 pb-2">تم إرسال رمز التحقق إلى رقم جوالك</p>
                                <button 
                                    onClick={() => setShowSuccess(false)}
                                    className="inline-flex items-center gap-2 px-4 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors"
                                >
                                    <span className="text-gray-600 font-medium">{phoneNumber}</span>
                                    <FaEdit className="w-3.5 h-3.5 text-main-900" />
                                </button>
                            </div>
                            <div className="flex justify-center gap-3 mt-6" dir="ltr">
                                {[3, 2, 1, 0].map((index) => (
                                    <input
                                        key={index}
                                        ref={el => codeInputRefs.current[3 - index] = el}
                                        type="text"
                                        inputMode="numeric"
                                        pattern="\d*"
                                        maxLength="1"
                                        value={verificationCode[3 - index]}
                                        onChange={(e) => handleVerificationCodeChange(3 - index, e.target.value)}
                                        onKeyDown={(e) => handleKeyDown(3 - index, e)}
                                        onPaste={(e) => handlePaste(e, 3 - index)}
                                        className="w-12 h-12 text-center text-2xl border-2 border-gray-300 rounded-xl focus:border-main-900 focus:outline-none transition-colors"
                                        dir="ltr"
                                    />
                                ))}
                            </div>
                            <button
                                className="w-full bg-main-900 text-white py-3 rounded-xl hover:bg-main-800 transition-colors duration-300 mt-6"
                            >
                                تأكيد
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </BottomSheet>
    );
}

export default LoginModal;
